// Import react elements
import { useEffect, useRef, useState } from 'react';

// Import components
import BulkMoveDropdown from './BulkMoveToFilter';
import CreateButton from 'global/components/CreateButton';

// Import utilities
import { DisplayConfirmationDialog } from "global/utilities/DialogBox";



// Displays actions when image/s are selected in the grid
const BulkActionBar = ({
    selectedImages,
    setSelectedImages,
    clearSelection,
    onDelete,
    updateSelectedImage,
    onUpdatesComplete,
    setThumbnailSize,
    sortOption,
    setSortOption,
    searchQuery,
    setSearchQuery,
    imageData, 
    setCurrentPage,
    filteredImages
}) => {



    // Set a state to show/hide the 'Move to filters' dropdown
    const [showMoveDropdown, setShowMoveDropdown] = useState(false);

    // Ref to track clicks outside the 'Move to filters' dropdown
    const moveWrapperRef = useRef(null);



    // Function to toggle thumbnail size
    const handleThumbnailSizeChange = (size) => {

        // Set the thumbnail size
        setThumbnailSize(size);

    };



    // Function to handle the submit button when move images to new type, cat and subcat
    const handleMoveSubmit = async (fields) => {

        // Format the data so we don't send empty values

        // Remove empty fields
        const cleanedFields = Object.fromEntries(
            Object.entries(fields).filter(([_, value]) => value !== '')
        );

        // Exit early if all fields were empty
        if (Object.keys(cleanedFields).length === 0) {
            return;
        }

        // Update all images and collect responses
        const responses = await Promise.all(

            // Map through the selected images and run the api call to update them
            selectedImages.map(id => 
                updateSelectedImage(id, { ...cleanedFields, id })

            )
        );

        // Collect an array of failed update ids and error messages
        const failedToUpdate = selectedImages.map((image, index) => {

            // Get the response for the current image update attempt
            const response = responses[index];

            // If the update failed
            return !response?.success

                // Return an object with the image ID and error message
                ? { id: response.id, error_message: response?.error_message || 'Unknown error' }

                // Otherwise, return null (indicating success, nothing to track)
                : null;

        })

        // Remove nulls (i.e. successful updates)
        .filter(Boolean);

        // Check whether at least one selected record was successfully updated.
        const anySuccess = responses.some((response) => response?.success);

        // Refresh the currently hydrated bucket level once after all successful bulk updates finish.
        if (anySuccess && onUpdatesComplete) {
            await onUpdatesComplete();
        }

        // Check if there are any failed updates so we can alert the user
        if (failedToUpdate.length > 0) {

            // Set the dialog message based on number of fails
            const message =
                `${failedToUpdate.length > 1 ? 'Some images' : 'Image'} could NOT be updated:<br/><br/>` +

                // new Set([...]) removes duplicate error mesages
                [...new Set(failedToUpdate.map(failedUpdate => failedUpdate.error_message))].join('<br/>');

            // Display dialog box to alert user that one or more of the images have failed to update
            DisplayConfirmationDialog(

                message,
                "Ok",
                "",

                async function (is_confirmed) {

                    // If user confirms
                    if (is_confirmed) {

                        // Update selectedImages to only those that failed
                        setSelectedImages(failedToUpdate.map(failedUpdate => failedUpdate.id));

                    }

                }

            );

        } else {

            // Clear the image/s selection because they have been updated
            clearSelection();

        }

        // Close the dropdown
        setShowMoveDropdown(false);

    };



    // Close dropdown when clicking outside
    const handleOutsideClick = (e) => {

        // Check if the click event is not the dropdown
        if (moveWrapperRef.current && !moveWrapperRef.current.contains(e.target)) {

            // Close the dropddown
            setShowMoveDropdown(false);

        }

    };

    // Attach outside click listener to close dropdown
    useEffect(() => {

        document.addEventListener('mousedown', handleOutsideClick);

        return () => document.removeEventListener('mousedown', handleOutsideClick);

    }, []);



    useEffect(() => {

        //console.log("reset the pagination: ", filteredImages)

        setCurrentPage(1);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredImages]);



    return (

        <div className="bulk-action-bar">

            <div className="horizontal-align-content-center">

                <div className="bulk-action-bar__sort">
                    <label>Size: </label>
                    <select defaultValue="m" onChange={(e) => handleThumbnailSizeChange(e.target.value)}>
                        <option value="s">Small</option>
                        <option value="m">Medium</option>
                        <option value="l">Large</option>
                    </select>
                </div>

                <div className="bulk-action-bar__sort">
                    <label>Sort by: </label>
                    <select value={sortOption} onChange={(e) => setSortOption(e.target.value)}>
                        <option value="newest">Newest</option>
                        <option value="oldest">Oldest</option>
                        <option value="filename-asc">Name A-Z</option>
                        <option value="filename-desc">Name Z-A</option>
                    </select>
                </div>



            </div>

            <div className="horizontal-align-content-center">

                {selectedImages.length > 0 &&

                    <div className="horizontal-align-content-center">

                        <div className="image-grid__tally">

                            <span>{selectedImages.length} selected </span>

                            <div className="clear_selected_tally" onClick={() => clearSelection([])}>
                                ⨯
                            </div>

                        </div>

                        {/* Move to filter button */}
                        <CreateButton title="Move" tooltip="Move Selected" size="s" onClick={() => setShowMoveDropdown(prev => !prev)} icon="/images/icon_move_filter.png" iconTitle="Move" />
                        
                        {/* Animated dropdown */}
                        {showMoveDropdown && (

                            <div className="bulk-action-bar__move-dropdown">
                                <BulkMoveDropdown
                                    imageData={imageData}
                                    handleMoveSubmit={handleMoveSubmit}
                                    onCancel={() => setShowMoveDropdown(false)}
                                    moveWrapperRef={moveWrapperRef}
                                />

                            </div>
                        )}

                        <CreateButton title="Delete" isDelete={true} tooltip="Delete Selected" size="s" onClick={onDelete} icon="/images/icon_bin.png" iconTitle="Delete" />

                    </div>
                }

                <div>

                    <input
                        type="text"
                        placeholder="Search by filename..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="bulk-action-bar__search"
                    />

                </div>

            </div>

        </div>

    );

};

export default BulkActionBar;
